import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PageHeader from '@/components/PageHeader'
import { WithRouter } from '@/components/WithRouter'
import { objToSearch } from '@/utils/helpers'
import { Card, Row, Col, Pagination, Spin, Avatar, Empty } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { SelectC } from '@/i2antdV5'
import ApiPollingManager from '@/utils/ApiPollingManager'
import StaffListModal from '@/views/commandCenter/share/StaffListModal'
import { renderLabel } from '@/utils/renderUtils'

import { fetchStaffList, resetStaffState } from '@/redux/dashboard/staffList/StaffSlice'
import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice'

const breadcrumbList = [
  { breadcrumbName: 'dashboard', href: '/client/commandCenter' },
  { breadcrumbName: '员工列表' }
]

const { Meta } = Card;

class StaffList extends React.Component {

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(fetchStoreList({
      serviceType: 'default',
      dataType: 'storeOption'
    }));
    // 从 URL 获取查询参数
    const query = this.getQuery();
    this.fetchData(query);
  }
  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(resetStaffState());
  }
  
    /**
   * 获取查询参数（排除 dataKey）
   * @returns {Object} 查询参数对象
   */
    getQuery() {
      const { location } = this.props;
      
      if (!location) {
        return {};
      }
      
      // WithRouter 已经将 location.search 解析为对象
      // 如果 location.search 是 undefined 或 null，使用空对象
      const searchObj = location.search || {};
      
      // 确保 searchObj 是对象类型
      if (typeof searchObj !== 'object' || Array.isArray(searchObj)) {
        return {};
      }
      
      const query = { ...searchObj };
      // 排除 dataKey
      delete query.dataKey;
      
      return query;
    }
  
    /**
     * 更新 URL 并重新获取数据
     * @param {Object} newQuery - 新的查询对象
     */
    updateUrlAndFetch = (newQuery) => {
      const { location, navigate } = this.props;
      navigate(`${location.pathname}${objToSearch(newQuery)}`, { replace: true });
      
      // 重新获取数据，传入新的 query（排除 dataKey）
      const queryForFetch = { ...newQuery };
      delete queryForFetch.dataKey;
      this.fetchData(queryForFetch);
    };
  
    /**
     * 更新查询参数
     * @param {string|Object} keyOrUpdates - 查询键或更新对象
     * @param {*} value - 查询值（当 keyOrUpdates 为字符串时）
     */
    updateQuery = (keyOrUpdates, value) => {
      const { location } = this.props;
      const currentSearch = location.search || {};
      const newQuery = { ...currentSearch };
      
      if (typeof keyOrUpdates === 'object') {
        // 批量更新
        Object.keys(keyOrUpdates).forEach((key) => {
          if (
            keyOrUpdates[key] !== undefined &&
            keyOrUpdates[key] !== null &&
            keyOrUpdates[key] !== ''
          ) {
            newQuery[key] = keyOrUpdates[key];
          } else {
            delete newQuery[key];
          }
        });
      } else {
        // 单个更新
        if (value !== undefined && value !== null && value !== '') {
          newQuery[keyOrUpdates] = value;
        } else {
          delete newQuery[keyOrUpdates];
        }
      }
      
      this.updateUrlAndFetch(newQuery);
    };
  
    
  fetchData = (query) => {
    const { dispatch } = this.props;
    // fetchStaffList 期望接收 { query, dataType } 格式
    dispatch(fetchStaffList({ query: query || {}, dataType: 'default' }));
  }

  // 根据状态过滤员工列表
  filterStaffByStatus = (staffList, status) => {
    if (!Array.isArray(staffList)) return [];
    return staffList.filter(item => {
      // 根据实际数据结构调整字段名，可能是 status、work_status、staff_status 等
      const itemStatus = item.status || item.work_status || item.staff_status || 'on_duty';
      return itemStatus === status;
    });
  }

  // 渲染员工卡片
  renderStaffCard = (item, index) => {
    return (
      <Col 
        key={item.id || item.user_id || item.staff_id || `staff-${index}`}
        xs={24}
        sm={12}
        md={10}
        lg={8}
        xl={4}
      >
      <StaffListModal
        key={item.id || item.user_id || item.staff_id || `staff-${index}`}
        baseProps={{
          fields: item
        }}
      >
        <Card.Grid
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.3s',
            boxSizing: 'border-box',
            height: '120px',
            border: '1px solid #f0f0f0',
            overflow: 'hidden',
          }}
          hoverable
        >
          <div style={{ 
            display: 'flex', 
            height: '100%',
            gap: '12px'
          }}>
            {/* 左侧：avatar 和 title */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <Avatar 
                size={48} 
                src={item.avatar} 
                icon={!item.avatar ? <UserOutlined /> : undefined} 
                style={{ marginBottom: '8px' }}
              />
              <span style={{ fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
                {item.user_name || item.name || '未知'}
              </span>
            </div>
            {/* 右侧：description */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'flex-start',
              lineHeight: '1.6'
            }}>
              <div style={{ 
                display: 'flex', 
                gap: '12px',
                marginBottom: '4px',
                fontSize: 12, 
                color: '#666'
              }}>
                {item.age && (
                  <span>{renderLabel('年龄')}{item.age}岁</span>
                )}
                {item.placeOfOrigin && (
                  <span>{renderLabel('籍贯')}{item.placeOfOrigin}</span>
                )}
              </div>
              {item.position_name && (
                <div style={{ fontSize: 12, color: '#666', marginBottom: '4px' }}>
                  {renderLabel('职位')}{item.position_name}
                </div>
              )}
              {item.advantages && (
                <div 
                  style={{ 
                    fontSize: 12, 
                    color: '#666',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {renderLabel('优势')}{item.advantages}
                </div>
              )}
            </div>
          </div>
        </Card.Grid>
      </StaffListModal>
      </Col>
    );
  }

  render() {
    const { storeObj, staffListObj } = this.props;
    const { default_storeOption: storeOption } = storeObj || {};
    const { 
      data: staffList = [], 
      total: staffTotal = 0, 
      pageNumber: staffPageIndex = 1, 
      pageSize: staffPageSize = 20,
      fetching = false
    } = staffListObj || {};
    
    // 获取当前查询参数，优先使用 Redux 中的 query，否则从 URL 获取
    const query = staffListObj?.query || this.getQuery() || {};
    
    // 处理数据结构：可能是数组，也可能是对象包含 data 字段
    const staffListData = Array.isArray(staffList) 
      ? staffList 
      : (staffList?.data || []);
    
    // 根据状态分类员工（如果后端返回的数据包含状态字段）
    // 如果没有状态字段，则所有员工都显示在"在岗员工"中
    // const onDutyStaff = Array.isArray(staffListData) 
    //   ? staffListData.filter(item => {
    //       const status = item.status || item.work_status || item.staff_status;
    //       return !status || status === 'on_duty' || status === '在职' || status === 1;
    //     })
    //   : [];
    
    // const trainingStaff = Array.isArray(staffListData)
    //   ? staffListData.filter(item => {
    //       const status = item.status || item.work_status || item.staff_status;
    //       return status === 'training' || status === '培训' || status === 2;
    //     })
    //   : [];
    
    // const vacationStaff = Array.isArray(staffListData)
    //   ? staffListData.filter(item => {
    //       const status = item.status || item.work_status || item.staff_status;
    //       return status === 'vacation' || status === '休假' || status === 3;
    //     })
    //   : [];
    
    return (
      <>
        <div>
          <PageHeader breadcrumbList={breadcrumbList} />
        </div>
        <div className='i2-container'>
          <Card className="i2-card-page">
            <Spin spinning={fetching}>
              <section className="i2-app-toolbar">
                <div className="toolbar-filters">
                  <SelectC
                    label=""
                    query={query}
                    searchKey="store_id"
                    options={storeOption}
                    action={this.updateQuery}
                  />
                </div>
              </section>
              <Row style={{ marginBottom: '24px' }}>
                <Col span={24}>
                  <Card title='在岗员工' style={{ marginBottom: '16px' }}>
                    <Row gutter={[16, 16]}>
                      {staffListData.length > 0 ? (
                        staffListData.map((item, index) => this.renderStaffCard(item, index))
                      ) : (
                        <Empty description="暂无在岗员工" />
                      )}
                    </Row>
                  </Card>
                </Col>
              </Row>
              <Row style={{ marginBottom: '24px' }}>
                <Col span={24}>
                  <Card 
                    title='培训员工' 
                    style={{ marginBottom: '16px' }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <Row gutter={[16, 16]}>
                      {staffListData.length > 0 ? (
                        staffListData.map((item, index) => this.renderStaffCard(item, index))
                      ) : (
                        <Empty description="暂无培训员工" />
                      )}
                    </Row>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Card 
                    title='休假员工'
                    bodyStyle={{ padding: '12px' }}
                  >
                    <Row gutter={[16, 16]}>
                      {staffListData.length > 0 ? (
                        staffListData.map((item, index) => this.renderStaffCard(item, index))
                      ) : (
                        <Empty description="暂无休假员工" />
                      )}
                    </Row>
                  </Card>
                </Col>
              </Row>
              {staffTotal > staffPageSize && (
                <Row style={{ marginTop: '24px' }}>
                  <Col span={24} style={{ textAlign: 'right' }}>
                    <Pagination
                      current={staffPageIndex}
                      pageSize={staffPageSize}
                      total={staffTotal}
                      showSizeChanger
                      showQuickJumper
                      showTotal={(total) => `共 ${total} 条`}
                      onChange={(page, size) => {
                        this.updateQuery({ pageNumber: page, pageSize: size });
                      }}
                      onShowSizeChange={(current, size) => {
                        this.updateQuery({ pageNumber: 1, pageSize: size });
                      }}
                    />
                  </Col>
                </Row>
              )}
            </Spin>
          </Card>
        </div>
      </>
    )
  }
}


const StaffListWrapper = (props) => {
  const dispatch = useDispatch();
  const allProps = {
    ...props,
    dispatch,
    storeObj: useSelector((state) => state.dashboard.storeList || {}),
    staffListObj: useSelector((state) => state.dashboard.staffList || {}),
  };
  return <StaffList {...allProps} />;
};

export default WithRouter(StaffListWrapper);
